<template>
  <div
    v-if="localLicense"
    :class="wrapperClass"
    data-testid="license-show-details"
  >
    <div class="d-flex flex-column pa-0 ga-4 ma-3">
      <v-row class="ma-0">
        <v-col
          :class="$vuetify.display.mdAndUp ? 'pa-0' : 'px-0'"
          class="ma-0"
          cols="12"
          md="7"
        >
          <StandardCard
            @title-action-clicked="handleEditLicense"
            :title-action="licenseDetailsTitleAction"
            :title-action-cols="4"
            card-test-id="license-details-section"
            class="license-detail-card"
            title="License details"
          >
            <div class="d-flex flex-column ga-4">
              <v-row no-gutters>
                <LabeledControl
                  :message-class="fieldLabelClass"
                  cols="6"
                  message="Licensing program name"
                >
                  <span
                    :class="fieldValueClass"
                    data-testid="license-program-value"
                  >
                    {{ $bct(localLicense.licensing_program_name || blankValue) }}
                  </span>
                </LabeledControl>

                <LabeledControl
                  :message-class="fieldLabelClass"
                  cols="6"
                  message="Date created"
                >
                  <LongDate
                    v-if="localLicense.created_at"
                    :date="localLicense.created_at"
                    date-classes="fs-16"
                  />

                  <span
                    v-else
                    :class="fieldValueClass"
                  >
                    {{ blankValue }}
                  </span>
                </LabeledControl>
              </v-row>

              <v-row no-gutters>
                <LabeledControl
                  :message-class="fieldLabelClass"
                  cols="6"
                  message="License number"
                >
                  <span
                    :class="fieldValueClass"
                    data-testid="license-number-value"
                  >
                    {{ localLicense.number || blankValue }}
                  </span>
                </LabeledControl>

                <LabeledControl
                  :message-class="fieldLabelClass"
                  cols="6"
                  message="Date started"
                >
                  <LongDate
                    v-if="localLicense.start_date"
                    :date="localLicense.start_date"
                    date-classes="fs-16"
                  />

                  <span
                    v-else
                    :class="fieldValueClass"
                  >
                    {{ blankValue }}
                  </span>
                </LabeledControl>
              </v-row>

              <v-row no-gutters>
                <LabeledControl
                  :message-class="fieldLabelClass"
                  cols="6"
                  message="Status"
                >
                  <span
                    :class="fieldValueClass"
                    data-testid="license-status-value"
                  >
                    {{ licenseStatusDisplay }}
                  </span>
                </LabeledControl>

                <LabeledControl
                  :message="expired ? 'Date expired' : 'Date expires'"
                  :message-class="fieldLabelClass"
                  cols="6"
                >
                  <LongDate
                    v-if="localLicense.end_date"
                    :date="localLicense.end_date"
                    date-classes="fs-16"
                  />

                  <span
                    v-else
                    :class="fieldValueClass"
                  >
                    {{ blankValue }}
                  </span>
                </LabeledControl>
              </v-row>
            </div>
          </StandardCard>
        </v-col>

        <v-col
          v-if="localLicense"
          :class="$vuetify.display.mdAndUp ? 'ps-5 pa-0' : 'px-0'"
          class="ma-0"
          cols="12"
          md="5"
        >
          <LicenseAssignmentCard
            @save="handleLicenseUpdate"
            :blank-value="blankValue"
            :license="localLicense"
          />
        </v-col>
      </v-row>

      <v-row
        v-if="localLicense.provider_id"
        class="ma-0"
      >
        <v-col
          class="pa-0"
          cols="12"
          md="7"
        >
          <StandardCard
            :title-action="providerTitleAction"
            :title-action-cols="4"
            card-test-id="license-provider-section"
            title="Provider information"
          >
            <v-row no-gutters>
              <v-col
                class="pe-4"
                cols="6"
              >
                <div class="d-flex flex-column ga-2">
                  <v-icon
                    class="material-icons-outlined"
                    color="black"
                    icon="storefront"
                  />

                  <span :class="fieldLabelClass">{{ $bct('Name') }}</span>

                  <span :class="fieldValueClass">
                    {{ $bct(localLicense?.provider_name || blankValue) }}
                  </span>
                </div>
              </v-col>

              <v-col cols="6">
                <div class="d-flex flex-column ga-2">
                  <v-icon
                    class="material-icons-outlined"
                    color="black"
                    icon="location_on"
                  />

                  <span :class="fieldLabelClass">{{ $bct('Location') }}</span>

                  <span :class="fieldValueClass">
                    {{ $bct(providerAddress || blankValue) }}
                  </span>
                </div>
              </v-col>
            </v-row>
          </StandardCard>
        </v-col>
      </v-row>
    </div>

    <ResourceDialog
      v-if="canEditLicense"
      @save="saveLicenseEdits"
      ref="editDialog"
      :fields="editLicenseFields"
      :processing="processing"
      content-test-id="license-edit-dialog"
      title="Edit license"
      closeable
    />
  </div>
</template>

<script setup>
import { useRoute } from 'vue-router';

import { formatAddress } from '@/shared/services/address';
import LabeledControl from '@/shared/components/form/LabeledControl.vue';
import { LICENSE_STATUS_OPTIONS, licenseStatusLabel } from '@/shared/assets/constants';
import LicenseAssignmentCard from '@/shared/components/licenses/LicenseAssignmentCard.vue';
import LongDate from '@/shared/components/LongDate.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useRole from '@/shared/composables/useRole';

const $bct = inject('translator');

const { scopedApi } = useApi();
const eventBus = useEventBus();
const { isSpecialist } = useRole();
const route = useRoute();

const props = defineProps({
  containerClass: {
    type: String,
    default: undefined,
  },
  license: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['save']);

const fieldLabelClass = 'c-black fw-800 fs-16';
const fieldValueClass = 'fs-16';

const blankValue = ref('--');
const editDialog = ref(null);
const localLicense = ref(null);
const processing = ref(false);
const provider = ref(null);

const canEditLicense = computed(() => isSpecialist && Boolean(scopedApi?.license?.update));

const editLicenseFields = computed(() => [
  { text: 'License number', value: 'number' },
  { text: 'Start date', value: 'start_date', type: 'date' },
  { text: 'End date', value: 'end_date', type: 'date' },
  {
    text: 'Status',
    value: 'status',
    items: LICENSE_STATUS_OPTIONS,
    itemText: 'name',
    itemValue: 'value',
    required: true,
  },
]);

const wrapperClass = computed(() =>
  ['d-flex', 'flex-column', props.containerClass].filter(Boolean),
);

const expired = computed(() => {
  return new Date(localLicense.value?.end_date) < new Date();
});

const licenseStatusDisplay = computed(() => {
  return licenseStatusLabel(localLicense.value?.status) || blankValue.value;
});

const providerAddress = computed(() => {
  if (!provider.value) return null;
  return formatAddress(
    provider.value.address1,
    provider.value.address2,
    provider.value.city,
    provider.value.state,
    provider.value.zip,
  );
});

const providerShowRoute = computed(() => {
  if (!localLicense.value?.provider_id) return undefined;

  return {
    name: 'ProviderShow',
    params: { providerId: localLicense.value.provider_id },
    query: { tab: 'licensing' },
  };
});

const licenseDetailsTitleAction = computed(() => {
  if (!canEditLicense.value) return null;

  return {
    prependIcon: 'edit',
    role: 'link',
    testId: 'license-edit-button',
    text: 'Edit',
  };
});

const providerTitleAction = computed(() => ({
  customClasses: 'c-primary flex-shrink-0',
  testId: 'license-provider-view-details',
  text: 'View details',
  // eslint-disable-next-line id-length -- StandardButton/vue-router prop name
  to: providerShowRoute.value,
  variant: 'text',
}));

function handleLicenseUpdate(updatedLicense) {
  localLicense.value = updatedLicense;
  emit('save', updatedLicense);
}

function handleEditLicense() {
  if (!canEditLicense.value) return;

  editDialog.value.open({
    number: localLicense.value.number ?? '',
    start_date: localLicense.value.start_date,
    end_date: localLicense.value.end_date,
    status: licenseStatusLabel(localLicense.value.status),
  });
}

async function saveLicenseEdits(formData) {
  if (!canEditLicense.value) return;

  processing.value = true;
  try {
    const response = await scopedApi.license.update(localLicense.value.id, {
      number: formData.number,
      start_date: formData.start_date,
      end_date: formData.end_date,
      status: formData.status,
    });
    if (response?.status !== 200) return;

    handleLicenseUpdate(response.data);
    eventBus.chime('Saved');
    await editDialog.value?.close();
  } finally {
    processing.value = false;
  }
}

async function load() {
  if (props.license) {
    localLicense.value = props.license;
  } else {
    await loadLicense();
  }

  await loadProvider();
}

async function loadLicense() {
  const response = await scopedApi.license.get(route.params.id);
  localLicense.value = response.data;
}

async function loadProvider() {
  if (!localLicense.value.provider_id) return;

  const response = await scopedApi.provider.get(localLicense.value.provider_id);
  provider.value = response?.data;
}

watch(
  () => props.license,
  (newLicense) => {
    if (newLicense) localLicense.value = newLicense;
  },
);

onMounted(load);
</script>
